import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button } from '../../components/UI/Button';
import { Modal } from '../../components/UI/Modal';
import { ImportModal } from '../../components/Import/ImportModal';
import ConfirmationDialog from '../../components/UI/ConfirmationDialog';
import { userService, CreateUserRequest, UpdateUserRequest } from '../../services/userService';
import { teamService } from '../../services/teamService';
import { User } from '../../types/users';
import { Profile } from '../../types/profiles';
import { Team } from '../../types/teams';
import { useToast } from '../../components/UI/ToastProvider';
import { debugLocalStorage } from '../../services/api';
import { UserAvatar } from '../../components/UI/UserAvatar';
import { generateUUID } from '../../utils/uuidUtils';
import { useAuth } from '../../contexts/AuthContext';
import {
  canAssignAdministratorProfile,
  getAssignableProfiles,
  isAdministratorProfile,
} from '../../utils/ownerCustomerAccess';
import { IMPORT_USERS_COMPLETED_EVENT } from '../../utils/importNotificationEvents';
import { extractCpfDigits, formatCpfDisplay, formatCpfInput } from '../../utils/cpfMask';

const statusOptions = ['Ativo', 'Inativo', 'Todos'];

export interface UsersPageProps {
  targetCustomerId?: string;
  targetCustomerIsOwner?: boolean;
  pageTitle?: string;
  showImport?: boolean;
  showTeamManagement?: boolean;
  showCustomerContext?: boolean;
  customerName?: string;
}

export const UsersPage: React.FC<UsersPageProps> = ({
  targetCustomerId,
  targetCustomerIsOwner,
  pageTitle = 'Gerenciamento de Usuários',
  showImport = true,
  showTeamManagement = true,
  showCustomerContext = false,
  customerName,
}) => {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isTeamsModalOpen, setIsTeamsModalOpen] = useState(false);
  const [selectedUserForTeams, setSelectedUserForTeams] = useState<User | null>(null);
  const [isBossForTeam, setIsBossForTeam] = useState<{ [teamId: string]: boolean }>({});
  const [teamsModalLoading, setTeamsModalLoading] = useState(false);
  const [profileFilter, setProfileFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('Ativo');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const pageSize = 10;
  
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    user: User | null;
  }>({
    isOpen: false,
    user: null
  });
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const { addToast } = useToast();
  const { user: authUser } = useAuth();
  const resolvedCustomerId = targetCustomerId || sessionStorage.getItem('@KnowInDoc:customer_id') || undefined;
  const currentCustomerId = sessionStorage.getItem('@KnowInDoc:customer_id') || undefined;
  const effectiveTargetCustomerIsOwner = targetCustomerIsOwner ?? Boolean(authUser?.isOwnerCustomer);
  const canManageTeams = showTeamManagement && (!targetCustomerId || targetCustomerId === currentCustomerId);

  // Form state
  const [form, setForm] = useState<{
    name: string;
    email: string;
    cpf: string;
    birthDate: string;
    password: string;
    roleId: string;
    isActive: boolean;
    authenticationType: string;
  }>({
    name: '',
    email: '',
    cpf: '',
    birthDate: '',
    password: '',
    roleId: '',
    isActive: true,
    authenticationType: 'local'
  });

  useEffect(() => {
    debugLocalStorage();
    
    // Carregar dados com delay para evitar problemas de concorrência
    const loadData = async () => {
      try {
        await fetchUsers();
        await new Promise(resolve => setTimeout(resolve, 100));
        await fetchProfiles();
        await new Promise(resolve => setTimeout(resolve, 100));
        await fetchTeams();
      } catch (error) {
        console.error('[UsersPage] Erro ao carregar dados:', error);
      }
    };
    
    loadData();
  }, [targetCustomerId]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search, targetCustomerId]);

  // Refetch users when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchUsers();
  }, [profileFilter, statusFilter, targetCustomerId]);

  // Refetch users when page changes
  useEffect(() => {
    fetchUsers();
  }, [currentPage, targetCustomerId]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      // Converter statusFilter para isActive
      let isActiveFilter: boolean | undefined;
      if (statusFilter === 'Ativo') {
        isActiveFilter = true;
      } else if (statusFilter === 'Inativo') {
        isActiveFilter = false;
      }
      // Se statusFilter for 'Todos', isActiveFilter permanece undefined
      
      const data = await userService.getUsers(
        search,
        currentPage,
        pageSize,
        profileFilter,
        isActiveFilter,
        targetCustomerId
      );
      
      setUsers(data.users);
      setTotalUsers(data.total);
      setTotalPages(Math.ceil(data.total / pageSize));
    } catch (error: any) {
      console.error('[UsersPage.fetchUsers] Erro ao buscar usuários:', error);
      console.error('[UsersPage.fetchUsers] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      addToast('Erro ao carregar usuários', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfiles = async () => {
    try {
      const data = await userService.getProfiles(resolvedCustomerId);
      setProfiles(data);
    } catch (error: any) {
      console.error('Erro ao buscar perfis:', error);
      addToast('Erro ao carregar perfis', 'error');
    }
  };

  const assignableProfiles = useMemo(
    () => getAssignableProfiles(profiles, effectiveTargetCustomerIsOwner),
    [profiles, effectiveTargetCustomerIsOwner]
  );

  const modalProfileOptions = useMemo(() => {
    if (!isEditMode || !editingUser?.isAdmin || effectiveTargetCustomerIsOwner)
    {
      return assignableProfiles;
    }

    const currentProfile = profiles.find(profile => profile.id === editingUser.role.id);
    if (currentProfile && !assignableProfiles.some(profile => profile.id === currentProfile.id))
    {
      return [...assignableProfiles, currentProfile];
    }

    return assignableProfiles;
  }, [assignableProfiles, isEditMode, editingUser, effectiveTargetCustomerIsOwner, profiles]);

  const fetchTeams = async () => {
    try {
      const data = await teamService.getTeams();
      setTeams(data);
    } catch (error: any) {
      console.error('[UsersPage.fetchTeams] Erro ao buscar equipes:', error);
      console.error('[UsersPage.fetchTeams] Detalhes do erro:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
      addToast('Erro ao carregar equipes', 'error');
    }
  };

  const fetchUsersRef = useRef(fetchUsers);
  fetchUsersRef.current = fetchUsers;

  useEffect(() => {
    const handleImportCompleted = () => {
      void fetchUsersRef.current();
    };

    window.addEventListener(IMPORT_USERS_COMPLETED_EVENT, handleImportCompleted);
    return () => {
      window.removeEventListener(IMPORT_USERS_COMPLETED_EVENT, handleImportCompleted);
    };
  }, []);

  const getStatusFromIsActive = (isActive: boolean): 'Ativo' | 'Inativo' => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const filteredUsers = users || [];

  // Log para debug do estado
  useEffect(() => {
    // Log para debug da renderização
  }, [users, filteredUsers, search, profileFilter, statusFilter, loading]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleFirstPage = () => {
    setCurrentPage(1);
  };

  const handleLastPage = () => {
    setCurrentPage(totalPages);
  };

  const handleFormChange = (field: string, value: any) => {
    setForm(prev => {
      const newForm = { ...prev, [field]: value };
      
      // Atualizar providerUserId automaticamente quando email ou authenticationType mudar
      if (field === 'email' && (newForm.authenticationType === 'local' || newForm.authenticationType === 'google')) {
        // newForm.providerUserId = value; // Removido
      }
      
      return newForm;
    });
  };

  const handleTeamSelection = (teamId: string) => {
    // Esta função não é mais necessária, mas mantemos para compatibilidade
  };

  const openCreateModal = () => {
    setIsEditMode(false);
    setEditingUser(null);
    setForm({
      name: '',
      email: '',
      cpf: '',
      birthDate: '',
      password: '',
      roleId: '',
      isActive: true,
      authenticationType: 'local'
    });
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const openEditModal = async (user: User) => {
    setIsEditMode(true);
    setEditingUser(user);
    setShowPassword(false);
    setIsModalOpen(true);
    setModalLoading(true);

    try
    {
      const freshUser = await userService.getUser(user.id);
      setEditingUser(freshUser);
      setForm({
        name: freshUser.name,
        email: freshUser.email,
        cpf: formatCpfDisplay(freshUser.cpf),
        birthDate: freshUser.birthDate ? String(freshUser.birthDate).slice(0, 10) : '',
        password: '',
        roleId: freshUser.role.id,
        isActive: freshUser.isActive,
        authenticationType: freshUser.authenticationType,
      });
    }
    catch (error: any)
    {
      console.error('[UsersPage.openEditModal] Erro ao buscar usuário:', error);
      addToast('Erro ao carregar dados do usuário para edição', 'error');
      setIsModalOpen(false);
    }
    finally
    {
      setModalLoading(false);
    }
  };

  const openTeamsModal = async (user: User) => {
    try {
      setTeamsModalLoading(true);
      
      // Limpar estado anterior
      setIsBossForTeam({});
      
      // Buscar dados atualizados do usuário
      const updatedUser = await userService.getUser(user.id);
      
      // Buscar informações sobre se o usuário é líder de cada equipe
      const bossStatus: { [teamId: string]: boolean } = {};
      
      if (updatedUser.teams && updatedUser.teams.length > 0) {
        for (const team of updatedUser.teams) {
          try {
            const teamDetails = await teamService.getTeam(team.id);
            if (teamDetails) {
              // Verificar se o usuário é líder da equipe
              bossStatus[team.id] = teamDetails.leader === user.id || team.isBoss;
            }
          } catch (error) {
            console.error(`[UsersPage.openTeamsModal] Erro ao buscar equipe ${team.id}:`, error);
          }
        }
      }
      
      setSelectedUserForTeams(updatedUser);
      setIsBossForTeam(bossStatus);
      setIsTeamsModalOpen(true);
    } catch (error: any) {
      console.error('[UsersPage.openTeamsModal] Erro ao buscar dados do usuário:', error);
      addToast('Erro ao carregar dados do usuário', 'error');
    } finally {
      setTeamsModalLoading(false);
    }
  };

  const handleAddTeam = async (userId: string, teamId: string, isBoss: boolean = false) => {
    try {
      const addedTeam = await teamService.addMember(teamId, userId, isBoss);
      addToast('Usuário adicionado à equipe com sucesso', 'success');
      
      // Atualizar o estado isBossForTeam para esta equipe
      setIsBossForTeam(prev => ({
        ...prev,
        [teamId]: isBoss
      }));
      
      // Atualizar o usuário selecionado no modal
      if (selectedUserForTeams) {
        setSelectedUserForTeams(prev => {
          if (!prev) return prev;
          const updatedTeams = [...prev.teams, { 
            id: teamId, 
            name: addedTeam.name, 
            description: addedTeam.description, 
            isBoss: isBoss 
          }];
          return {
            ...prev,
            teams: updatedTeams
          };
        });
      }
      
      // Atualizar a lista de usuários
      fetchUsers();
    } catch (error: any) {
      console.error('[UsersPage.handleAddTeam] Erro detalhado:', {
        error: error,
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      const message = error.response?.data?.message || error.message || 'Erro ao adicionar usuário à equipe';
      addToast(message, 'error');
    }
  };

  const handleUpdateTeamMember = async (userId: string, teamId: string, isBoss: boolean) => {
    try {
      await teamService.updateTeamMember(teamId, userId, isBoss);
      addToast('Status do usuário na equipe atualizado com sucesso', 'success');
      
      // Atualizar o estado local do usuário selecionado
      if (selectedUserForTeams) {
        setSelectedUserForTeams(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            teams: prev.teams.map(team => 
              team.id === teamId 
                ? { ...team, isBoss: isBoss }
                : team
            )
          };
        });
      }
      
      // Atualizar a lista de usuários
      fetchUsers();
    } catch (error: any) {
      console.error('Erro ao atualizar status do usuário na equipe:', error);
      const message = error.response?.data?.message || 'Erro ao atualizar status do usuário na equipe';
      addToast(message, 'error');
      
      // Reverter o estado local em caso de erro
      if (selectedUserForTeams) {
        setSelectedUserForTeams(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            teams: prev.teams.map(team => 
              team.id === teamId 
                ? { ...team, isBoss: !isBoss } // Reverter para o estado anterior
                : team
            )
          };
        });
      }
    }
  };

  const handleRemoveTeam = async (userId: string, teamId: string) => {
    try {
      await teamService.removeMember(teamId, userId);
      addToast('Usuário removido da equipe com sucesso', 'success');
      
      // Limpar o estado isBossForTeam para esta equipe
      setIsBossForTeam(prev => {
        const newState = { ...prev };
        delete newState[teamId];
        return newState;
      });
      
      // Atualizar a lista de usuários
      fetchUsers();
      
      // Atualizar o usuário selecionado no modal
      if (selectedUserForTeams) {
        setSelectedUserForTeams(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            teams: prev.teams.filter(team => team.id !== teamId)
          };
        });
      }
    } catch (error: any) {
      console.error('Erro ao remover usuário da equipe:', error);
      const message = error.response?.data?.message || 'Erro ao remover usuário da equipe';
      addToast(message, 'error');
    }
  };

  const handleIsBossChange = (teamId: string, isBoss: boolean) => {
    setIsBossForTeam(prev => ({
      ...prev,
      [teamId]: isBoss
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalLoading(true);

    try {
      // Validar senha se for preenchida
      if (form.password && form.password.length > 20) {
        addToast('A senha deve ter no máximo 20 caracteres', 'error');
        setModalLoading(false);
        return;
      }

      const cpfDigits = extractCpfDigits(form.cpf);
      if (cpfDigits && cpfDigits.length !== 11)
      {
        addToast('Informe um CPF válido com 11 dígitos ou deixe o campo em branco.', 'error');
        setModalLoading(false);
        return;
      }

      const identityPayload = {
        cpf: cpfDigits || null,
        birthDate: form.birthDate.trim() ? form.birthDate.trim() : null,
      };

      const selectedProfile = profiles.find(profile => profile.id === form.roleId);
      const isSelectingAdministrator =
        isAdministratorProfile(selectedProfile) ||
        (editingUser?.role?.code === 'admin' && form.roleId === editingUser.role.id);

      if (
        isSelectingAdministrator &&
        !canAssignAdministratorProfile(effectiveTargetCustomerIsOwner)
      )
      {
        addToast(
          'O perfil Administrador só pode ser atribuído a usuários do cliente proprietário da plataforma.',
          'error'
        );
        setModalLoading(false);
        return;
      }

      if (isEditMode && editingUser) {
        const updateData: UpdateUserRequest = {
          name: form.name,
          email: form.email,
          roleId: form.roleId,
          isActive: form.isActive,
          authenticationType: form.authenticationType,
          ...identityPayload,
        };

        if (form.password) {
          updateData.password = form.password;
        }

        await userService.updateUser(editingUser.id, updateData);
        addToast('Usuário atualizado com sucesso', 'success');
      } else {
        const currentCustomerId = targetCustomerId || resolvedCustomerId || generateUUID();
        const newUserId = generateUUID();
        
        const createData: CreateUserRequest = {
          customerId: currentCustomerId,
          userId: newUserId,
          name: form.name,
          email: form.email,
          password: form.password,
          roleId: form.roleId,
          isActive: form.isActive,
          authenticationType: form.authenticationType,
          providerUserId: form.email,
          requireEmailConfirmation: form.authenticationType === 'local',
          ...identityPayload,
        };

        await userService.createUser(createData, targetCustomerId);
        addToast('Usuário criado com sucesso', 'success');
      }

      setIsModalOpen(false);
      fetchUsers();
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error);
      
      const message = error.response?.data?.detail || 'Erro ao salvar usuário';
      addToast(message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async (user: User) => {
    if (user.isAdmin) {
      addToast('Usuários administradores não podem ser excluídos', 'error');
      return;
    }

    // Abrir modal de confirmação
    setDeleteConfirmation({
      isOpen: true,
      user: user
    });
  };

  const getProfileName = (user: User) => {
    return user.role?.name || 'Perfil não definido';
  };

  const getTeamNames = (user: User) => {
    if (!user.teams || !Array.isArray(user.teams)) return '';
    return user.teams.map(team => team.name).join(', ');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="flex-1">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
          {showCustomerContext && customerName && (
            <p className="mt-1 text-sm text-gray-600">
              Cliente selecionado: <span className="font-medium text-gray-900">{customerName}</span>
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {showImport && (
            <Button variant="secondary" onClick={() => setIsImportModalOpen(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Importar
            </Button>
          )}
          <Button onClick={openCreateModal}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Usuário
          </Button>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <div className="flex">
            <div className="relative mr-4">
              <input
                type="text"
                placeholder="Buscar usuários..."
                className="border rounded-md pl-10 pr-4 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={search}
                onChange={handleSearch}
              />
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <select
              className="border rounded-md pl-4 pr-8 py-2 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500 mr-4"
              value={profileFilter}
              onChange={e => setProfileFilter(e.target.value)}
            >
              <option value="">Todos os perfis</option>
              {assignableProfiles.map(profile => (
                <option key={profile.id} value={profile.id}>{profile.name}</option>
              ))}
            </select>
            <select
              className="border rounded-md pl-4 pr-8 py-2 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              {statusOptions.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Carregando usuários...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12">
              <div className="mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {search || profileFilter || statusFilter !== 'Ativo' 
                  ? 'Nenhum usuário encontrado' 
                  : 'Nenhum usuário cadastrado'
                }
              </h3>
              <p className="text-gray-500 mb-6">
                {search || profileFilter || statusFilter !== 'Ativo'
                  ? 'Tente ajustar os termos de busca ou filtros aplicados.'
                  : 'Comece criando seu primeiro usuário para gerenciar o sistema.'
                }
              </p>
              {!search && !profileFilter && statusFilter === 'Ativo' && (
                <Button onClick={openCreateModal}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Criar usuário
                </Button>
              )}
            </div>
          ) : (
            <>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="py-3 px-4 text-left text-gray-600 font-medium">Nome</th>
                    <th className="py-3 px-4 text-left text-gray-600 font-medium">Email</th>
                    <th className="py-3 px-4 text-left text-gray-600 font-medium">Perfil</th>
                    <th className="py-3 px-4 text-left text-gray-600 font-medium">Autenticação</th>
                    <th className="py-3 px-4 text-left text-gray-600 font-medium">Status</th>
                    <th className="py-3 px-4 text-left text-gray-600 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <UserAvatar
                            userId={user.id}
                            name={user.name || 'Usuário'}
                            avatarBase64={user.avatarBase64}
                            size="md"
                            className="mr-3"
                          />
                          <span>{user.name || 'Nome não informado'}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">{user.email || 'Email não informado'}</td>
                      <td className="py-3 px-4">
                        <span 
                          className="bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full cursor-help"
                          title={user.role?.description || 'Sem descrição'}
                        >
                          {user.role?.name || 'Perfil não definido'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={
                          user.authenticationType === 'local' ? 'bg-green-100 text-green-800' :
                          user.authenticationType === 'ad' ? 'bg-blue-100 text-blue-800' :
                          user.authenticationType === 'ldap' ? 'bg-purple-100 text-purple-800' :
                          user.authenticationType === 'azure' ? 'bg-indigo-100 text-indigo-800' :
                          user.authenticationType === 'google' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        + ' text-xs font-medium px-3 py-1 rounded-full'}>
                          {user.providerName || 'N/A'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={
                          user.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-200 text-gray-700'
                        + ' text-xs font-medium px-3 py-1 rounded-full'}>
                          {user.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <Button 
                            variant="secondary" 
                            size="sm"
                            onClick={() => openEditModal(user)}
                            title="Editar usuário"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </Button>
                          {canManageTeams && (
                            <Button 
                              variant="secondary" 
                              size="sm"
                              onClick={() => openTeamsModal(user)}
                              title="Gerenciar equipes"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </Button>
                          )}
                          {!user.isAdmin && (
                            <Button 
                              variant="danger" 
                              size="sm"
                              onClick={() => handleDelete(user)}
                              title="Excluir usuário"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Controles de Paginação */}
              {totalPages > 1 && (
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Mostrando {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalUsers)} de {totalUsers} usuários
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Botão Primeira Página */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleFirstPage}
                      disabled={currentPage === 1}
                      title="Primeira página"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                      </svg>
                    </Button>
                    
                    {/* Botão Página Anterior */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      title="Página anterior"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </Button>
                    
                    {/* Números das Páginas */}
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "primary" : "secondary"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className="min-w-[40px]"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    {/* Botão Próxima Página */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      title="Próxima página"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Button>
                    
                    {/* Botão Última Página */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleLastPage}
                      disabled={currentPage === totalPages}
                      title="Última página"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M6 5l7 7-7 7" />
                      </svg>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        title={isEditMode ? "Editar Usuário" : "Novo Usuário"} 
        onClose={() => setIsModalOpen(false)}
      >
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <div className="relative">
              <input 
                type="text" 
                className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  form.name.length >= 100 ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
                value={form.name}
                onChange={e => handleFormChange('name', e.target.value.slice(0, 100))}
                required
                maxLength={100}
              />
              <div className="absolute -bottom-6 right-0 text-xs text-gray-500">
                {form.name.length}/100
              </div>
            </div>
            {form.name.length >= 100 && (
              <p className="text-xs text-red-500 mt-1">
                Limite máximo de 100 caracteres atingido
              </p>
            )}
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input 
              type="email" 
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.email}
              onChange={e => handleFormChange('email', e.target.value)}
              required
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">CPF</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000.000.000-00"
                maxLength={14}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.cpf}
                onChange={e => handleFormChange('cpf', formatCpfInput(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data de nascimento</label>
              <input
                type="date"
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.birthDate}
                onChange={e => handleFormChange('birthDate', e.target.value)}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Perfil</label>
              <select 
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.roleId}
                onChange={e => handleFormChange('roleId', e.target.value)}
                required
              >
                <option value="">Selecione um perfil</option>
                {modalProfileOptions.map(profile => (
                  <option key={profile.id} value={profile.id}>{profile.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select 
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.isActive ? 'Ativo' : 'Inativo'}
                onChange={e => handleFormChange('isActive', e.target.value === 'Ativo')}
              >
                <option value="Ativo">Ativo</option>
                <option value="Inativo">Inativo</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Autenticação</label>
              <select 
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.authenticationType}
                onChange={e => {
                  const authType = e.target.value;
                  handleFormChange('authenticationType', authType);
                  
                  // Atualizar providerUserId baseado no tipo de autenticação
                  if (authType === 'local') {
                    // handleFormChange('providerUserId', form.email); // Removido
                  } else if (authType === 'ad' || authType === 'ldap' || authType === 'azure') {
                    // handleFormChange('providerUserId', ''); // Removido
                  } else if (authType === 'google') {
                    // handleFormChange('providerUserId', form.email); // Removido
                  }
                  
                  // Desabilitar confirmação de email para AD, LDAP, Azure e Google
                  if (authType === 'ad' || authType === 'ldap' || authType === 'azure' || authType === 'google') {
                    // handleFormChange('requireEmailConfirmation', false); // Removido
                  } else {
                    // handleFormChange('requireEmailConfirmation', true); // Removido
                  }
                }}
                required
              >
                <option value="local">E-mail</option>
                <option value="ad">Active Directory (AD)</option>
                <option value="ldap">LDAP</option>
                <option value="azure">Azure AD</option>
                <option value="google">Google</option>
              </select>
            </div>
          </div>
          
          <div className="mb-4 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
            <input 
              type={showPassword ? 'text' : 'password'} 
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
              value={form.password}
              onChange={e => handleFormChange('password', e.target.value)}
              required={!isEditMode}
              maxLength={20}
              autoComplete="off"
            />
            <button 
              type="button" 
              onClick={() => setShowPassword(s => !s)} 
              className="absolute right-3 top-8 text-gray-500 focus:outline-none"
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10 0-1.657.403-3.221 1.125-4.575M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
            {isEditMode && (
              <p className="text-xs text-gray-500 mt-1">
                Deixe em branco para manter a senha atual.
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Máximo de 20 caracteres
            </p>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => setIsModalOpen(false)}
              disabled={modalLoading}
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              disabled={modalLoading}
            >
              {modalLoading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Equipes */}
      <Modal 
        isOpen={isTeamsModalOpen} 
        title={`Equipes de ${selectedUserForTeams?.name || 'Usuário'}`}
        onClose={() => setIsTeamsModalOpen(false)}
      >
        {teamsModalLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Carregando dados do usuário...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Equipes Atuais</h3>
              {selectedUserForTeams?.teams && selectedUserForTeams.teams.length > 0 ? (
                <div className="space-y-2">
                  {selectedUserForTeams.teams.map(team => (
                    <div key={team.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <span className="font-medium">{team.name || 'Equipe não encontrada'}</span>
                        <div className="mt-2">
                          <label className="flex items-center">
                            <input 
                              type="checkbox" 
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={isBossForTeam[team.id] || team.isBoss || false}
                              onChange={e => {
                                const newIsBoss = e.target.checked;
                                handleIsBossChange(team.id, newIsBoss);
                                // Chamar a API para atualizar o status
                                if (selectedUserForTeams) {
                                  handleUpdateTeamMember(selectedUserForTeams.id, team.id, newIsBoss);
                                }
                              }}
                            />
                            <span className="ml-2 text-sm text-gray-700">Líder da equipe</span>
                          </label>
                        </div>
                      </div>
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => {
                          if (selectedUserForTeams) {
                            handleRemoveTeam(selectedUserForTeams.id, team.id);
                          }
                        }}
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Usuário não está em nenhuma equipe.</p>
              )}
            </div>

            {/* Mostrar seção "Adicionar Equipe" apenas se há equipes disponíveis */}
            {(() => {
              const availableTeams = teams.filter(team => 
                !selectedUserForTeams?.teams?.some(userTeam => userTeam.id === team.id)
              );
              
              if (availableTeams.length > 0) {
                return (
                  <div className="border-t pt-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Adicionar Equipe</h3>
                    <div className="space-y-2">
                      {availableTeams.map(team => (
                        <div key={team.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                          <div className="flex-1">
                            <span className="font-medium">{team.name}</span>
                            <p className="text-sm text-gray-600">{team.description}</p>
                            <div className="mt-2">
                              <label className="flex items-center">
                                <input 
                                  type="checkbox" 
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  checked={isBossForTeam[team.id] || false}
                                  onChange={e => handleIsBossChange(team.id, e.target.checked)}
                                />
                                <span className="ml-2 text-sm text-gray-700">Definir como líder da equipe</span>
                              </label>
                            </div>
                          </div>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => {
                              if (selectedUserForTeams) {
                                handleAddTeam(selectedUserForTeams.id, team.id, isBossForTeam[team.id] || false);
                              } else {
                                console.error('[UsersPage] selectedUserForTeams é null');
                                addToast('Erro: dados do usuário não encontrados', 'error');
                              }
                            }}
                          >
                            Adicionar
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, user: null })}
        onConfirm={async () => {
          if (deleteConfirmation.user) {
            try {
              await userService.deleteUser(deleteConfirmation.user.id);
              addToast('Usuário excluído com sucesso', 'success');
              fetchUsers();
            } catch (error: any) {
              console.error('Erro ao excluir usuário:', error);
              const message = error.response?.data?.message || 'Erro ao excluir usuário';
              addToast(message, 'error');
            }
          }
          setDeleteConfirmation({ isOpen: false, user: null });
        }}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir o usuário "${deleteConfirmation.user?.name || ''}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {showImport && (
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          entityType="users"
        />
      )}
    </div>
  );
}; 